<template>
	<AriaRegion :class="['chat-list', { 'has-sender-filter': showSenderFilter, 'is-empty': isEmpty }]" :id="resolvedId" :heading-level="resolvedHeadingLevel" :heading-style="resolvedHeadingStyle" :tag="resolvedTag" v-bind="$attrs">
		<template #header>
			<slot name="header-left" v-bind="scope" />
			
			<!-- Unfortunately, this empty <span> is required in order to get the <AriaRegion> to recognize the fact that its 'header' slot has content. -->
			<span v-if="showSenderFilter" aria-hidden="true"></span>
			
			<SelectField v-if="showSenderFilter" v-model="senderFilter" :disabled="!usesGlobalState && (isInitializing || isLoading)" aria-label="Filter messages">
				<template #options>
					<option	v-for="(value, index) in availableSenderFilters" :key="`sender-filter-option-${index}`" :value="value">
						{{ getSenderFilterOptionText(value) }}
					</option>
				</template>
			</SelectField>
			<slot name="header-right" v-bind="scope" />
		</template>

		<template #default>
			<div :class="['chat-list-content', isEmpty ? 'null-message' : 'card']">
				<template v-if="senderFilter === 'studios' && (isDeactivated)">
					<p>To view messages from studios, you must upgrade to a paid account.</p>
					<UpgradeButton class="button-inline">Upgrade Now</UpgradeButton>
				</template>
				<template v-else-if="isInitializing">
					<slot name="loading">
						<p>Loading...</p>
					</slot>
				</template>
				<template v-else-if="isEmpty">
					<slot name="empty">
						<p>You don't have any messages {{ availableSenderFilters.length > 1 && senderFilter !== 'all' ? `from ${senderFilter}` : '' }} yet.</p>
					</slot>
				</template>
				<template v-else>
				<slot name="chats" v-bind="scope">
					<router-link v-for="record of resolvedChats"
						:to="{ name: $routes.Thread.name, params: { jobId: record.job.id, chatId: record.chat.id },}"
						class="chat-list-item"
						:class="{ 'is-unread': record.unreadMessagesCount > 0 }"
						:key="record.chat.id"
					>
					<template v-if="chatRequiresUpgrade(record)">
						<ProfileAvatar :profile="record.chatUserProfile" size="40" />
						<div class="chat-list-item-name">{{ record.chatUserProfile.name }}</div>
						<div class="chat-list-item-event">To view this message, you must upgrade to a paid account.</div>
						<div class="chat-list-item-date">{{ timeAgo(record) }}</div>
					</template>
					<template v-else>
						<ProfileAvatar :profile="record.chatUserProfile" size="40" />
						<div class="chat-list-item-name">{{ record.chatUserProfile.name }}</div>
						<div class="chat-list-item-event">{{ record.job.event.name }}</div>
						<div class="chat-list-item-date">{{ timeAgo(record) }}</div>
					</template>
					</router-link>
				</slot>
				<BaseButton
					v-if="showLoadMoreButton"
					class="chat-list-load-button"
					:disabled="isLoading"
					@click="loadChats"
				>
					Load More Messages
				</BaseButton>
				</template>
				<slot name="after" v-bind="scope" />
			</div>
		</template>
	</AriaRegion>
</template>

<script>
import { DateTime } from 'luxon';
import {
  capitalize,
  toGuid,
  toHtmlHeadingLevel,
  toHtmlId,
  toInteger,
  toString,
} from '@/helpers/convert';

export default {
  name: 'ChatList',
  inheritAttrs: false,
  props: {
    /**
     * An optional array containing the chats that are shown. This component will never attempt
     * to load chats from the API while this property is non-null (even if it's an empty array).
     *
     * @type {import('vue/types/options').PropOptions<readonly ChatRecord[] | null | undefined>}
     */
    chats: {
      type: Array,
      default: null,
    },
    /**
     * An optional filter that only shows chats from a specific type of sender.
     * - `"all"` shows chats from all users.
     * - `"photographer"` only shows chats from Associates, Second Shooters, and Lead Shooters.
     * - `"studios"` only shows chats from Studios.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    from: {
      default: null,
    },
    /**
     * An integer between 1 and 6 that corresponds to the **tag name** of the heading element in
     * which the `heading` text is rendered, expressed as either a `string` or `number`. If
     * empty, then the `heading` text (if present) is used as the region element's `aria-label`
     * attribute instead.
     *
     * Note that this property specifies the *tag name* (`1` for `<h1>`, `2` for `<h2>`, etc.),
     * which should be representative of the document outline. To style the heading to *look
     * like* a different heading level, use the `headingStyle` property instead.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    headingLevel: {
      default: null,
    },
    /**
     * An integer between 1 and 6 that corresponds to the **style** of the heading element in
     * which the `heading` text is rendered, expressed as either a `string` or `number`. If
     * empty, then the heading style is not overridden.
     *
     * Note that this property specifies the *CSS class* (`1` for `heading-1`, `2` for `heading-2`,
     * etc.), which is used to override the heading style to that of a different heading level
     * (and is independent of the document outline). To specify the *tag name* for the heading
     * element, use the `headingStyle` property instead.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    headingStyle: {
      default: null,
    },
    /**
     * The `id` attribute of the root element.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    id: {
      default: null,
    },
    /**
     * The optional job ID for which chats are filtered.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    jobId: {
      default: null,
    },
    /**
     * `true` if this chat list is the page's main content; otherwise, `false`.
     *
     * @type {import('vue/types/options').PropOptions<boolean | null | undefined>}
     */
    main: {
      type: Boolean,
      default: false,
    },
    /**
     * The maximum total number of chats to show.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    max: {
      default: null,
    },
    /**
     * If `true`, then the "From" filter element is hidden.
     *
     * @type {import('vue/types/options').PropOptions<boolean | null | undefined>}
     */
    noFromFilter: {
      type: Boolean,
      default: false,
    },
    /**
     * The maximum number of chats to fetch per request.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    pageSize: {
      default: null,
    },
    /**
     * The optional search query.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    query: {
      default: null,
    },
    /**
     * The tag name of the component's root element. This *should* be one of the HTML5 sectioning
     * element tag names (but this constraint is not enforced):
     * - `article`
     * - `aside`
     * - `footer`
     * - `header`
     * - `main`
     * - `nav`
     * - `section`
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    tag: {
      default: null,
    },
  },
  data() {
    return {
      /** @type {ChatRecord[]} */
      chatsData: [],
      /** @type {boolean} */
      isLoadingData: false,
      /** @type {number | null} */
      pendingReset: null,
      /** @type {ChatSenderFilter | null} */
      senderFilterData: null,
      /** @type {number} */
      today: new Date().setHours(0, 0, 0, 0),
      /** @type {number | null} */
      todayInterval: null,
      /** @type {number | null} */
      totalData: null,
      /** @type {number | null} */
      version: null,
    };
  },
  computed: {
	freeJob() {
			return this.$store.getters['jobs/freeJobData'].freeJob;
		},
    /**
     * @returns {readonly ChatSenderFilter[]}
     */
    availableSenderFilters() {
      return this.$store.getters['chats/availableSenderFilters'];
    },
    /**
     * @returns {boolean}
     */
    isDeactivated() {
      return !!this.subscription?.isDeactivated;
    },
    /**
     * @returns {boolean}
     */
    isEmpty() {
      return this.resolvedChats.length <= 0;
    },
    /**
     * @returns {boolean}
     */
    isInitializing() {
      return this.isPrePopulated
        ? false
        : this.usesGlobalState
        ? this.isLoading && this.total == null
        : this.pendingReset != null || (this.isLoading && this.version == null);
    },
    isLoading: {
      /**
       * @returns {boolean}
       */
      get() {
        return this.isPrePopulated
          ? false
          : this.usesGlobalState
          ? this.$store.getters[`chats/${this.senderFilter}/isLoading`]
          : !!this.isLoadingData;
      },
      /**
       * @param {boolean} value
       * @returns {void}
       */
      set(value) {
        this.isLoadingData = !!value;
      },
    },
    /**
     * @returns {boolean}
     */
    isPrePopulated() {
      return Array.isArray(this.chats);
    },
    /**
     * @returns {boolean}
     */
    isTrialing() {
      return !!this.subscription?.isTrialing;
    },
    /**
     * The number of remaining items that haven't yet been loaded.
     *
     * @returns {number}
     */
    remainingLoadCount() {
      return this.isPrePopulated
        ? 0
        : Math.max(
            0,
            Math.min(this.resolvedMax, this.total ?? Number.POSITIVE_INFINITY) -
              this.resolvedChats.length
          );
    },
    /**
     * @returns {readonly ChatRecord[]}
     */
    resolvedChats() {
      /** @type {readonly ChatRecord[]} */
      let chats;
      if (this.usesGlobalState) {
        chats = this.$store.getters[`chats/${this.senderFilter}/chats`];
      } else if (this.isPrePopulated) {
        chats = Array.from(this.chats);
        /** @type {((record: ChatRecord) => boolean)[]} */
        const filters = [];
        const { resolvedJobId: jobId, senderFilter } = this;
        if (jobId) {
          filters.push(({ job }) => job.id === jobId);
        }
        if (senderFilter !== 'all') {
          const userId = this.$activeUser?.id;
          if (userId) {
            filters.push(
              senderFilter === 'studios'
                ? ({ job }) => job.creatorId !== userId
                : ({ job }) => job.creatorId === userId
            );
          }
        }
        if (filters.length) {
          chats = chats.filter((record) => filters.every((filter) => filter(record)));
        }
      } else {
        chats = this.chatsData;
      }
      const max = this.resolvedMax;
      return chats.length > max ? chats.slice(0, max) : chats;
    },
    /**
     * @returns {unknown}
     */
    resolvedHeadingLevel() {
      const level = toHtmlHeadingLevel(this.headingLevel);
      return level || (this.main ? 1 : 2);
    },
    /**
     * @returns {unknown}
     */
    resolvedHeadingStyle() {
      const level = toHtmlHeadingLevel(this.headingStyle);
      return level || (this.main ? 2 : null);
    },
    /**
     * @returns {string | null}
     */
    resolvedId() {
      return toHtmlId(this.id) || (this.main ? 'content' : null);
    },
    /**
     * @returns {JobId | null}
     */
    resolvedJobId() {
      return toGuid(this.jobId, null);
    },
    /**
     * @returns {number}
     */
    resolvedMax() {
      return toInteger(this.max, 0) || Number.POSITIVE_INFINITY;
    },
    /**
     * @returns {number}
     */
    resolvedPageSize() {
      return Math.min(toInteger(this.pageSize, 0) || 10, this.resolvedMax);
    },
    /**
     * @returns {string | null}
     */
    resolvedQuery() {
      return toString(this.input, null);
    },
    /**
     * @returns {string}
     */
    resolvedTag() {
      return this.main ? 'main' : toString(this.tag) || 'section';
    },
    /**
     * @returns {{
     *   readonly chats: readonly ChatRecord[];
     *   readonly isLoading: boolean;
     *   readonly isTrialing: boolean;
     *   readonly senderFilter: ChatSenderFilter;
     *   readonly today: number;
     *   readonly total: number;
     * }}
     */
    scope() {
      return {
        chats: this.resolvedChats,
        isDeactivated: this.isDeactivated,
        isLoading: this.isLoading,
        isTrialing: this.isTrialing,
        senderFilter: this.senderFilter,
        today: this.today,
        total: this.total,
      };
    },
    senderFilter: {
      /**
       * @returns {ChatSenderFilter}
       */
      get() {
        return (
          this.senderFilterProp ||
          this.toSenderFilter(this.senderFilterData) ||
          (this.usesGlobalState
            ? this.$store.getters['chats/senderFilter']
            : this.availableSenderFilters[0])
        );
      },
      /**
       * @param {ChatSenderFilter} value
       * @returns {void}
       */
      set(value) {
        if (!(value = this.toSenderFilter(value))) {
          return;
        }
        /** @type {ChatSenderFilter | null} */
        let prop;
        if (this.usesGlobalState) {
          this.$store.commit(`chats/setSenderFilter`, value);
        } else if (!(prop = this.senderFilterProp)) {
          this.senderFilterData = value;
        } else if (prop !== value) {
          // This will only update the prop if it is bound with `.sync`; otherwise it has no effect.
          this.$emit('update:from', value);
        }
      },
    },
    /**
     * @returns {ChatSenderFilter | null}
     */
    senderFilterProp() {
      return this.toSenderFilter(this.from);
    },
    /**
     * @returns {boolean}
     */
    showLoadMoreButton() {
      return !this.isEmpty && !this.isPrePopulated && !!this.remainingLoadCount;
    },
    /**
     * @returns {boolean}
     */
    showSenderFilter() {
      return !this.noFromFilter && this.availableSenderFilters.length > 1;
    },
    /**
     * @returns {Subscription | null}
     */
    subscription() {
      return this.$store.getters['subscriptions/current'];
    },
    total: {
      /**
       * @returns {number | null}
       */
      get() {
        return this.isPrePopulated
          ? this.resolvedChats.length
          : this.usesGlobalState
          ? this.$store.getters[`chats/${this.senderFilter}/total`]
          : this.totalData;
      },
      /**
       * @param {number | null} total
       * @returns {void}
       */
      set(total) {
        this.totalData = toInteger(total, 0);
      },
    },
    /**
     * @returns {boolean}
     */
    usesGlobalState() {
      return !this.isPrePopulated && !this.resolvedJobId && !this.resolvedQuery;
    },
  },
  methods: {
	chatRequiresUpgrade(item) {
			const isFreeJob = this.freeJob && this.freeJob.id == item.job.id;

			// No upgrade if I created it
			if (item.job.creatorId == this.$activeUser.id) {
				return false;
			}
			
			// If I'm a trial account
			if (this.isTrialing && !isFreeJob && !item.isJobBooked) {
				return true;
			}

			return false;
		},
    /**
     * @returns {void}
     */
    detachGlobalState() {
      this.$store.commit(`chats/${this.senderFilter}/detachListComponent`, this);
    },
    /**
     * @param {ChatSenderFilter} senderFilter
     * @returns {string}
     */
    getSenderFilterOptionText(senderFilter) {
      return `From ${capitalize(senderFilter)}`;
    },
    /**
     * @returns {Promise<void>}
     */
    async loadChats() {
      if (this.isPrePopulated || this.isLoading) {
        return;
      }

      if (this.pendingReset != null) {
        clearTimeout(this.pendingReset);
        this.pendingReset = null;
        if (this.chatsData.length || this.total != null) {
          const nextVersion = (this.version || 0) + 1;
          this.chatsData = [];
          this.totalData = null;
          this.version = Number.isSafeInteger(nextVersion) ? nextVersion : 0;
        }
      }

      const pageSize = Math.min(this.resolvedPageSize, this.remainingLoadCount);
      if (pageSize < 1) {
        return;
      }

      const store = this.$store;
      this.isLoading = true;
      try {
        await Promise.all([
          store.dispatch('subscriptions/getMySubscription'),
          store.dispatch('users/getActiveUser'),
        ]);

        if (this.usesGlobalState) {
          return store.dispatch('chats/getGlobalLatestChats', { pageSize });
        }

        const chats = this.chatsData;
        const initialVersion = this.version;
        const jobId = this.resolvedJobId;
        const offset = chats.length;
        const query = this.resolvedQuery;

        /** @type {GetLatestChatsPayload} */
        const payload = Object.create(null);
        if (pageSize < Number.POSITIVE_INFINITY) {
          payload.limit = pageSize;
        }
        if (offset) {
          payload.offset = offset;
        }
        if (jobId) {
          payload.jobId = jobId;
        }
        if (query) {
          payload.input = query;
        }
        /** @type {List<ChatRecord> | undefined} */
        const newChats = await store.dispatch('chats/getLatestChats', payload);
        if (!newChats || this.version !== initialVersion) {
          return;
        }
        const total = toInteger(newChats.total, 0);
        if (total != null) {
          this.total = total;
        }
        if (newChats.length <= 0) {
          return;
        }
        chats.push(...newChats);
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * @param {number} [delay]
     * @returns {void}
     */
    queueReset(delay) {
      clearTimeout(this.pendingReset);
      this.pendingReset = setTimeout(this.reset.bind(this), delay || 100);
    },
    /**
     * @returns {void}
     */
    reset() {
      clearTimeout(this.pendingReset);
      this.pendingReset = null;
      if (this.chatsData.length || this.total != null) {
        const nextVersion = (this.version || 0) + 1;
        this.chatsData = [];
        this.totalData = null;
        this.version = Number.isSafeInteger(nextVersion) ? nextVersion : 0;
      }
      this.loadChats();
    },
    /**
     * @param {ChatRecord} record
     * @returns {string}
     */
    timeAgo(record) {
      const { chat, lastMessage } = record;
      /** @type {import('luxon').DateTime | null | undefined} */
      let date;
      /** @type {DateConvertible | null | undefined} */
      let source;
      if (lastMessage) {
        source = lastMessage.updatedAt || lastMessage.createdAt;
      }
      if (
        (source == null && (source = chat.updatedAt || chat.createdAt) == null) ||
        !(date = this.$toDateTime(source, { zone: this.$systemTimeZone }))
      ) {
        return '';
      }
      /** @type {Intl.DateTimeFormatOptions} */
      let options;
      const today = DateTime.fromMillis(this.today, { zone: this.$systemTimeZone });
      if (date.year !== today.year) {
        options = DateTime.DATE_MED;
      } else if (date.month !== today.month || date.day !== today.day) {
        options = { day: 'numeric', month: 'short' };
      } else {
        options = DateTime.TIME_SIMPLE;
      }
      return date.toLocaleString(options);
    },
    /**
     * @param {unknown} value
     * @returns {ChatSenderFilter | null}
     */
    toSenderFilter(value) {
      return (value = toString(value).toLowerCase()) && this.availableSenderFilters.includes(value)
        ? value
        : null;
    },
    /**
     * @returns {void}
     */
    updateGlobalState() {
      if (!this.usesGlobalState) {
        return;
      }
      this.$store.commit(`chats/${this.senderFilter}/updateListComponent`, {
        component: this,
        pageSize: this.resolvedPageSize,
      });
      const count = this.resolvedChats.length;
      /** @type {number | null} */
      let total;
      if (count < this.resolvedPageSize && ((total = this.total) == null || count < total)) {
        this.loadChats();
      }
    },
    /**
     * @returns {void}
     */
    updateToday() {
      this.today = DateTime.fromMillis(Date.now(), {
        zone: this.$systemTimeZone,
      })
        .startOf('day')
        .toMillis();
    },
  },
  created() {
    this.todayInterval = setInterval(this.updateToday.bind(this), 5000);
    this.updateToday();
    this.updateGlobalState();
  },
  mounted() {
    this.loadChats();
  },
  beforeDestroy() {
    clearInterval(this.todayInterval);
    if (this.usesGlobalState) {
      this.detachGlobalState();
    }
  },
  watch: {
    /**
     * @param {boolean} isDeactivated
     */
    isDeactivated(isDeactivated) {
      if (!isDeactivated) {
        this.queueReset();
      }
    },
    /**
     * @param {boolean} isPrePopulated
     */
    isPrePopulated(isPrePopulated) {
      if (!isPrePopulated) {
        this.queueReset();
      }
    },
    /**
     * @param {boolean} isTrialing
     */
    isTrialing(isTrialing) {
      if (!isTrialing) {
        this.queueReset();
      }
    },
    resolvedJobId: 'queueReset',
    resolvedMax: 'queueReset',
    resolvedPageSize: 'updateGlobalState',
    resolvedQuery() {
      this.queueReset(500);
    },
    /**
     * @param {ChatSenderFilter} newSenderFilter
     * @param {ChatSenderFilter | undefined} oldSenderFilter
     */
    senderFilter(newSenderFilter, oldSenderFilter) {
      if (this.usesGlobalState) {
        if (oldSenderFilter) {
          this.$store.commit(`chats/${oldSenderFilter}/detachListComponent`, this);
        }
        this.updateGlobalState();
      } else {
        this.queueReset();
      }
    },
    /**
     * @param {boolean} usesGlobalState
     * @param {boolean | undefined} wasUsingGlobalState
     */
    usesGlobalState(usesGlobalState, wasUsingGlobalState) {
      if (usesGlobalState) {
        this.updateGlobalState();
      } else if (wasUsingGlobalState) {
        this.detachGlobalState();
      }
    },
  },
};
</script>
